<script setup lang="ts" generic="T extends { title: string; description: string; icon: string }">
/**
 * Grille de cards "Ouvrir" partagée entre la home (`CatalogPage`) et la
 * liste des agents (`AgentsListView`).
 *
 * Le composant ne connaît pas la cible de navigation : il émet `open` avec
 * la card cliquée et le parent route.
 */
defineProps<{
  cards: readonly T[];
}>();

const emit = defineEmits<{
  open: [card: T];
}>();
</script>

<template>
  <div class="home-card-grid">
    <article
      v-for="card in cards"
      :key="card.title"
      class="home-card"
      @click="emit('open', card)"
    >
      <div class="home-card-icon">{{ card.icon }}</div>
      <div class="home-card-body">
        <h3 class="home-card-title">{{ card.title }}</h3>
        <p class="home-card-description">{{ card.description }}</p>
      </div>
      <div class="home-card-footer">
        <button class="home-card-btn" @click.stop="emit('open', card)">
          Ouvrir
          <span class="home-card-btn-arrow">&rarr;</span>
        </button>
      </div>
    </article>
  </div>
</template>

<style scoped>
.home-card-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.5rem;
}

.home-card {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  transition: box-shadow 0.2s ease, border-color 0.2s ease, transform 0.2s ease;
  cursor: pointer;
}

.home-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  border-color: #2563eb;
  transform: translateY(-2px);
}

.home-card-icon {
  font-size: 2rem;
  line-height: 1;
}

.home-card-body {
  flex: 1;
}

.home-card-title {
  font-size: 1.125rem;
  font-weight: 600;
  margin-bottom: 0.375rem;
  color: #111827;
}

.home-card-description {
  font-size: 0.875rem;
  color: #6b7280;
  line-height: 1.5;
}

.home-card-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-top: 0.75rem;
  border-top: 1px solid #f3f4f6;
}

.home-card-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 1rem;
  background: #2563eb;
  color: #ffffff;
  font-size: 0.875rem;
  font-weight: 500;
  border-radius: 8px;
  transition: background-color 0.15s ease;
}

.home-card-btn:hover {
  background: #1d4ed8;
}

.home-card-btn-arrow {
  transition: transform 0.15s ease;
}

.home-card-btn:hover .home-card-btn-arrow {
  transform: translateX(2px);
}
</style>
